import axios from '~/axios';
import get from 'lodash/get';
import mapValues from 'lodash/mapValues';
import forOwn from 'lodash/forOwn';
import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import { stringify as stringifyQs } from 'query-string';
import { arrayed } from "mythil/array";
import { mockupCancel } from '~/data/cancelToken';
import {
    prepareResults, prepareOptions,
    sanitizeRawItem, sanitizeItemResponse, prepareCustomActionResponse
} from "~/data/dataPreparer";

const CancelToken = axios.CancelToken;
import { default as  defaultAuthenticatedRequest} from "~/backend/authenticatedRequest";

const prepareFilters = (filters) => {
    let preparedFilters = {};

    for (let filterName in filters) {
        if (!filters.hasOwnProperty(filterName)) {
            continue;
        }

        let filterValue = filters[filterName];

        if(isArray(filterValue)) {
            filterValue = filterValue.join(',');
        }

        if(isObject(filterValue)) {
            forOwn(filterValue, (val, prop) => {
                preparedFilters[`f${prop}_${filterName}`] = val;
            });

            continue;
        }

        preparedFilters[`f_${filterName}`] = filterValue;
    }

    return preparedFilters;
};

const getParamQueryString = (params) => {
    let queryParams = {
        page: params.page,
        sort: get(params, 'sortOptions.field', get(params, 'defaultSortOptions.field', undefined)),
        direction: get(params, 'sortOptions.direction', get(params, 'defaultSortOptions.direction', undefined)),
        rows: params.perPage,
        columns: params.columns || undefined,
        ...prepareFilters(params.filters ? params.filters : {})
    };

    return stringifyQs(queryParams);
}

export default function modelApi(baseUrl, model, authenticatedRequest = defaultAuthenticatedRequest) {
    let modelUrl = `${baseUrl}/${model}`,
        optionsUrl = `${modelUrl}/update_options`;

    const performItemAction = async (method, itemId, data = {}, prepareFoo = null) => {
        if(!prepareFoo) {
            prepareFoo = (data) => sanitizeItemResponse(data);
        }

        if(method && method.length) {
            method = '/'+method;
        }

        let saveUrl = `${modelUrl}/${itemId}${method}`;
        let requestFoo = async () => (await axios.post(saveUrl, data)).data;

        let returnData = await authenticatedRequest(requestFoo);

        returnData = prepareFoo(returnData);
        returnData.itemId = itemId;

        return returnData;
    };

    const performActionWithResults = async (params, action) => {
        let actionResult = await action();

        if(!actionResult.success) { return { data: actionResult } }

        try {
            let results = await fetchResults(params);
            return {
                data: actionResult,
                results: results
            }
        } catch(error) {
            return {
                data: actionResult,
                results: null,
                resultsError: error
            }
        }
    };

    const fetchResults = async (params, cancel = mockupCancel) => {
        if(cancel.isMockup) {
            console.error('FETICHING RESULTS WITH MOCKUP CANCEL!');
        }

        let itemIdPath = params.itemId ? `/${params.itemId}` : '';

        let queryString = getParamQueryString(params);

        cancel.cancel();

        let requestFoo = async () => (await axios.get(`${modelUrl}${itemIdPath}/results?${queryString}`, {
            cancelToken: new CancelToken(cancel.set)
        })).data;

        let resultsValue = await authenticatedRequest(requestFoo);

        cancel.reset();


        return prepareResults(resultsValue);
    };

    const saveItem = async (itemId, values, cancel = mockupCancel) => {
        return performItemAction('save', itemId, values);
    };

    const deleteItem = async (itemId) => {
        return performItemAction('delete',itemId, {}, (data) => {
            return prepareCustomActionResponse(data);
        });
    };

    const performCustomItemAction = async(itemId, actionName, data = {}) => {

        return performItemAction('custom_action', itemId, { action_name: actionName, data }, prepareCustomActionResponse);
    };

    const performCustomAction = async(actionName, params, data = {}) => {
        let queryString = getParamQueryString(params),
            actionUrl = `${modelUrl}/custom_action?${queryString}`,
            requestFoo = async () =>  (await axios.post(actionUrl, {action_name: actionName, data})).data,
            returnData = await authenticatedRequest(requestFoo);

        return prepareCustomActionResponse(returnData);
    };

    const performCustomActionAndFetchResults = async(actionName, params) => {
        return performActionWithResults(params, () => performCustomAction(actionName));
    };

    const uploadFile = async (fieldName, file, onUploadProgress = () => {}) => {
        const config = {
            onUploadProgress: function(progressEvent) {
                onUploadProgress(Math.round( (progressEvent.loaded * 100) / progressEvent.total));
            }
        };

        const url = `${modelUrl}/${fieldName}/file_upload`;

        let data = new FormData();
        data.append('file', file);

        let requestFoo = async () => (await axios.post(url, data, config)).data;
        let returnData = await authenticatedRequest(requestFoo);

        return returnData;
    };

    return {
        fetchResults, saveItem,
        deleteItem, performCustomAction, uploadFile,
        performCustomItemAction, performCustomActionAndFetchResults,


        /**
         *
         * @param fields - array of many fields to fetch options for. for every field provide term, page, field, type, selectedItems
         * @param cancel
         * @returns {Promise.<void>}
         */
        fetchOptions: async(fields, cancel = mockupCancel) => {
            fields = arrayed(fields);

            cancel.cancel('cancel');
            let requestFoo = async () => (await axios.post(optionsUrl, {fields}, {
                cancelToken: new CancelToken(cancel.set)
            })).data;

            let fieldWithOptions = await authenticatedRequest(requestFoo)

            cancel.reset();

            return mapValues(fieldWithOptions, prepareOptions);
        },

        fetchItem: async (itemId, cancel = mockupCancel) => {
            let itemUrl = `${modelUrl}/${itemId}`;

            cancel.cancel();
            let requestFoo = async () => (await axios.get(itemUrl, {
                cancelToken: new CancelToken(cancel.set)
            })).data;

            let itemData = await authenticatedRequest(requestFoo);

            cancel.reset();
            itemData.itemId = itemId;

            return sanitizeRawItem(itemData);
        },
    }

}